/**
 * The external (non-Bedrock) model registry — today just Jev, typesafe.ai's decision
 * model used by the DoBox executor. Mirrors the shape of `aiBedrockModels.ts`'s catalog
 * so `calculateUsdCost` can fall back to this one when the Bedrock catalog doesn't
 * resolve an id, but it is a separate list on purpose: a Jev id must never collide with
 * a Bedrock `idMatch`, and `bedrockModelCatalogCoverage.test.ts` only scans for
 * `anthropic.claude` / `amazon.nova` / `amazon.titan-embed` substrings, so it never sees
 * these ids at all.
 */

import { AiModelPriceRates } from './aiBedrockModels';
import { AiModelFamily } from './aiUsage';

export enum AiExternalModelKey {
  JEV = 'JEV',
}

export interface AiExternalModelCatalogEntry {
  key: AiExternalModelKey;
  /** Substring matched against a lowercased modelId, mutually exclusive with every other entry's. */
  idMatch: string;
  family: AiModelFamily;
  prices: AiModelPriceRates;
}

/**
 * Pinned Jev model version — never `jev-latest`. A floating alias would let typesafe.ai
 * change pricing and behavior under us with no corresponding entry in the catalog below.
 */
export const EXTERNAL_DECISION_MODEL = {
  JEV: 'jev-1.13.0',
} as const;

// Source: https://docs.typesafe.ai/models.md (typesafe.ai's own published rate — input
// only, no output/cache pricing published for this model). Retrieved 2026-10-02.
export const AI_EXTERNAL_MODEL_CATALOG: ReadonlyArray<AiExternalModelCatalogEntry> =
  [
    {
      key: AiExternalModelKey.JEV,
      idMatch: 'jev-',
      family: AiModelFamily.JEV,
      prices: {
        inputPricePerMillionUsd: 0.042,
        outputPricePerMillionUsd: 0,
        cacheReadPricePerMillionUsd: 0,
        cacheWritePricePerMillionUsd: 0,
      },
    },
  ];

export const resolveExternalModelCatalogEntry = (
  modelId: string,
  catalog: ReadonlyArray<AiExternalModelCatalogEntry> = AI_EXTERNAL_MODEL_CATALOG,
): AiExternalModelCatalogEntry | undefined => {
  const normalizedModelId = modelId.toLowerCase();

  return catalog.find((entry) => normalizedModelId.includes(entry.idMatch));
};
